import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import {
  CircleUserRound, Compass, Home, MapPin, Navigation, Server, ShoppingCart,
  Sparkles, ChevronRight, ArrowLeft, Wrench, BriefcaseBusiness, WalletCards,
  PackageOpen, Wifi, AlertTriangle, BookOpen,
} from 'lucide-react';
import { PRODUCTS, TRAVEL_GEAR, inventoryCapacityUsed, travelClassFor } from '../../game/constants.js';
import { wholeHealth } from '../../game/format.js';
import {
  V2_NEIGHBORHOODS, locationMeta, locationPublicTags, neighborhoodForPresentation,
  neighborhoodLocations, neighborhoodMeta, specialLocations, homeArtForState, travelAvailability,
} from './worldMetadata.js';
import { V2AdventurePanel } from './V2AdventurePanel.jsx';
import { GameWindow, resetGameWindowGeometry } from './GameWindow.jsx';
import { V2InventoryWindow } from './V2InventoryWindow.jsx';
import { V2HustleWindow } from './V2HustleWindow.jsx';
import { V2NotebookWindow } from './V2NotebookWindow.jsx';
import { V2SafehouseWindow } from './V2SafehouseWindow.jsx';
import { V2RunHistoryWindow } from './V2RunHistoryWindow.jsx';
import { V2BulletinWindow } from './V2BulletinWindow.jsx';
import { AccountSettings } from '../../components/AccountSettings.jsx';
import { ServerInfoModal } from '../../components/ServerInfoModal.jsx';
import './GameShellV2.css';

const V2MarketView = lazy(() => import('./V2MarketView.jsx'));
const money = (value) => `$${Math.max(0, Number(value || 0)).toLocaleString()}`;

function periodLabel(gameState) {
  return gameState?.worldState?.period?.label || 'Daytime';
}

function routeLabel(currentId, destinationId) {
  if (currentId === destinationId) return 'You are here';
  const trip = travelClassFor(currentId, destinationId);
  return trip === 'local' ? 'Local trip · no day advance' : 'Regional travel · day advances';
}

function careerCost(gameState, destination, mode = 'normal') {
  if (gameState?.gameMode?.id !== 'career' && gameState?.gameModeId !== 'career') return null;
  if (destination.id === 'home') return 0;
  const bucket = travelClassFor(gameState.currentLocationId, destination.id) === 'local' ? 'local' : 'cross_neighborhood';
  const costs = gameState?.career?.travelCosts || { local: { normal: 2 }, cross_neighborhood: { normal: 15, skateboard: 10, bicycle: 5, chaz: 0 } };
  return Number(costs?.[bucket]?.[mode] ?? 0);
}

function certaintyLabel(row, fallback = 'KNOWN') {
  const raw = String(row?.certainty || row?.kind || row?.type || row?.sourceType || fallback).toUpperCase();
  if (/RUMOR|GOSSIP/.test(raw)) return 'RUMOR';
  if (/HEARD|OVERHEARD/.test(raw)) return 'HEARD';
  if (/SIGHT|SPOTTED|SEEN/.test(raw)) return 'SIGHTING';
  return 'KNOWN';
}

function NeighborhoodArt({ meta, mode = 'thumb' }) {
  const source = mode === 'banner' ? meta?.banner : meta?.thumbnail;
  if (source) return <img src={source} alt={`${meta.name} neighborhood`} loading="lazy" />;
  return <div className={`v2-neighborhood-placeholder ${meta?.placeholder || 'generic'}`} aria-label={`${meta?.name || 'Neighborhood'} artwork placeholder`}><MapPin /><strong>{meta?.name || 'Unknown area'}</strong><span>Neighborhood art coming later</span></div>;
}

function Stat({ label, value, tone = '' }) {
  return <div className={tone}><span>{label}</span><strong>{value}</strong></div>;
}

function compactQuantity(value) {
  const numeric = Number(value || 0);
  return numeric.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

function InventoryStrip({ gameState, capacityUsed, onOpen }) {
  const drugSlots = PRODUCTS.map((product) => ({
    id: product.id,
    label: product.name,
    value: compactQuantity(gameState?.inventory?.[product.id]),
    unit: product.unitShort || 'g',
    empty: Number(gameState?.inventory?.[product.id] || 0) <= 0,
  }));
  const streetSlots = [
    { id: 'smokes', label: 'Smokes', value: compactQuantity(gameState?.streetInventory?.smokes), unit: 'pk', empty: Number(gameState?.streetInventory?.smokes || 0) <= 0 },
    { id: 'beer', label: 'Beer', value: compactQuantity(gameState?.streetInventory?.beer), unit: 'can', empty: Number(gameState?.streetInventory?.beer || 0) <= 0 },
  ];
  const junkCount = Object.values(gameState?.barterInventory || {}).reduce((sum, value) => sum + Number(value || 0), 0);
  const slots = [...drugSlots, ...streetSlots, { id: 'junk', label: 'Junk', value: compactQuantity(junkCount), unit: 'pc', empty: junkCount <= 0 }];
  return <section className="v2-inventory-strip" aria-label="Carried inventory">
    <button className="v2-pack-label" onClick={onOpen} title="Open inventory and stable tools"><PackageOpen /><span>PACK</span><b>{capacityUsed.toFixed(1).replace(/\.0$/, '')}/{gameState.capacity}g</b></button>
    <div className="v2-pack-slots">{slots.map((slot) => <div key={slot.id} className={`v2-pack-slot ${slot.empty ? 'empty' : ''}`} title={`${slot.label}: ${slot.value} ${slot.unit}`}><span>{slot.label}</span><strong>{slot.value}<small>{slot.unit}</small></strong></div>)}</div>
  </section>;
}

function StatusTags({ tags }) {
  return <div className="v2-tags">{tags.map((tag) => <span key={tag} data-tag={tag}>{tag}</span>)}</div>;
}

function NeighborhoodCard({ meta, current, onOpen }) {
  return <button className={`v2-neighborhood-card ${current ? 'current' : ''}`} onClick={onOpen}><div className="v2-card-art"><NeighborhoodArt meta={meta} /></div><div className="v2-card-copy"><span>{current ? 'CURRENT AREA' : 'NEIGHBORHOOD'}</span><h3>{meta.name}</h3><p>{meta.shortDescription || meta.description}</p><small>{(meta.locationIds || []).length} known locations</small></div><ChevronRight /></button>;
}

function LocationCard({ location, gameState, onOpen }) {
  const availability = travelAvailability(location, gameState);
  if (availability.hidden) return null;
  const current = location.id === gameState.currentLocationId;
  const disabled = current || !availability.selectable;
  const tags = locationPublicTags(location, gameState);
  return <button className={`v2-location-card ${current ? 'current' : ''} ${disabled ? 'locked' : ''}`} onClick={disabled ? undefined : onOpen} disabled={disabled}><div><span>{current ? 'YOU ARE HERE' : availability.selectable ? routeLabel(gameState.currentLocationId, location.id) : availability.reason}</span><h3>{location.shortName}</h3><p>{location.description}</p><StatusTags tags={tags} /></div>{!disabled && <ChevronRight />}</button>;
}

function TravelWindowContent({ gameState, onTravel, busy }) {
  const presentationNeighborhood = neighborhoodForPresentation(gameState.currentLocationId) || neighborhoodMeta('mt_gate');
  const [tab, setTab] = useState('nearby');
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [travelMode, setTravelMode] = useState('normal');

  const selectLocation = (location) => {
    const overall = travelAvailability(location, gameState);
    if (!overall.selectable) return;
    setSelectedLocation(location);
    const candidates = ['normal', 'skateboard', 'bicycle', 'chaz'];
    setTravelMode(candidates.find((mode) => travelAvailability(location, gameState, mode).selectable) || 'normal');
  };
  const ownedGear = Object.entries(TRAVEL_GEAR).filter(([id]) => gameState?.travelGear?.[id]?.owned);
  const chaz = gameState?.travelServices?.chaz || {};
  const cross = selectedLocation ? travelClassFor(gameState.currentLocationId, selectedLocation.id) !== 'local' : false;
  const modes = selectedLocation ? [['normal', 'On foot'], ...(cross ? ownedGear.map(([id, gear]) => [id, gear.name]) : []), ...(cross && chaz.active && Number(chaz.ridesRemaining || 0) > 0 ? [['chaz', `Chaz (${chaz.ridesRemaining} rides)`]] : [])].map(([id, label]) => ({ id, label, availability: travelAvailability(selectedLocation, gameState, id) })) : [];
  const selectedAvailability = selectedLocation ? travelAvailability(selectedLocation, gameState, travelMode) : { selectable: false, reason: '' };

  const go = async () => {
    if (!selectedLocation || selectedLocation.id === gameState.currentLocationId || !selectedAvailability.selectable) return;
    const ok = await onTravel(selectedLocation.id, travelMode);
    if (ok !== false) {
      setSelectedLocation(null);
      setSelectedNeighborhood(null);
      setTab('nearby');
      setTravelMode('normal');
    }
  };

  return <div className="v2-travel-window-content" aria-label="World navigator">
    {selectedLocation ? <div className="v2-destination-preview">
      <button className="v2-back-link" onClick={() => setSelectedLocation(null)}><ArrowLeft /> Back</button>
      <div className="v2-preview-heading"><span>DESTINATION</span><h2>{selectedLocation.shortName}</h2><p>{selectedLocation.description}</p><StatusTags tags={locationPublicTags(selectedLocation, gameState)} /></div>
      <div className="v2-preview-stats"><Stat label="Route" value={routeLabel(gameState.currentLocationId, selectedLocation.id)} /><Stat label="Danger" value={`${Math.round(Number(selectedLocation.dangerLevel || 0) * 100)}%`} /><Stat label="Police" value={`${Math.round(Number(selectedLocation.policeLevel || 0) * 100)}%`} /><Stat label="Fare" value={careerCost(gameState, selectedLocation, travelMode) == null ? 'Debt/travel rules apply' : money(careerCost(gameState, selectedLocation, travelMode))} /></div>
      {selectedLocation.availableProducts?.length > 0 && <div className="v2-market-summary"><span>KNOWN MARKET</span>{selectedLocation.availableProducts.map((id) => <b key={id}>{PRODUCTS.find((p) => p.id === id)?.name || id}</b>)}</div>}
      {modes.length > 1 && <div className="v2-mode-picker"><span>HOW ARE YOU GETTING THERE?</span>{modes.map(({ id, label, availability }) => <button key={id} disabled={!availability.selectable} className={travelMode === id ? 'active' : ''} onClick={() => setTravelMode(id)}><span>{label}{!availability.selectable && <em>{availability.reason}</em>}</span>{careerCost(gameState, selectedLocation, id) != null && <small>{money(careerCost(gameState, selectedLocation, id))}</small>}</button>)}</div>}
      {!selectedAvailability.selectable && selectedLocation.id !== gameState.currentLocationId && <div className="v2-travel-lock">LOCKED · {selectedAvailability.reason}</div>}
      <button className="v2-go-button" disabled={busy || selectedLocation.id === gameState.currentLocationId || !selectedAvailability.selectable} onClick={go}>{busy ? 'TRAVELING…' : selectedLocation.id === gameState.currentLocationId ? 'YOU ARE ALREADY HERE' : !selectedAvailability.selectable ? 'TRAVEL UNAVAILABLE' : `GO TO ${selectedLocation.shortName.toUpperCase()}`}</button>
    </div> : selectedNeighborhood ? <div className="v2-neighborhood-overview">
      <button className="v2-back-link" onClick={() => setSelectedNeighborhood(null)}><ArrowLeft /> Neighborhoods</button>
      <div className="v2-neighborhood-banner"><NeighborhoodArt meta={neighborhoodMeta(selectedNeighborhood)} mode="banner" /><div><span>NEIGHBORHOOD</span><h2>{neighborhoodMeta(selectedNeighborhood).name}</h2><p>{neighborhoodMeta(selectedNeighborhood).shortDescription}</p></div></div>
      <div className="v2-location-list">{neighborhoodLocations(selectedNeighborhood).map((location) => <LocationCard key={location.id} location={location} gameState={gameState} onOpen={() => selectLocation(location)} />)}</div>
    </div> : <><nav className="v2-travel-tabs">{[['nearby', 'Nearby'], ['neighborhoods', 'Neighborhoods'], ['special', 'Special']].map(([id, label]) => <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>{label}</button>)}</nav><div className="v2-travel-body">
      {tab === 'nearby' && <><div className="v2-neighborhood-mini"><NeighborhoodArt meta={presentationNeighborhood} /><div><span>YOU ARE IN</span><h3>{presentationNeighborhood.name}</h3><p>{presentationNeighborhood.shortDescription}</p></div></div><div className="v2-location-list">{neighborhoodLocations(presentationNeighborhood.id).map((location) => <LocationCard key={location.id} location={location} gameState={gameState} onOpen={() => selectLocation(location)} />)}</div></>}
      {tab === 'neighborhoods' && <div className="v2-neighborhood-list">{Object.values(V2_NEIGHBORHOODS).map((meta) => <NeighborhoodCard key={meta.id} meta={{ ...neighborhoodMeta(meta.id), ...meta }} current={meta.id === presentationNeighborhood.id} onOpen={() => setSelectedNeighborhood(meta.id)} />)}</div>}
      {tab === 'special' && <div className="v2-location-list">{specialLocations().map((location) => <LocationCard key={location.id} location={location} gameState={gameState} onOpen={() => selectLocation(location)} />)}</div>}
    </div></>}
  </div>;
}

export function GameShellV2({ gameState, runMeta, config, session, hubState, actionBusy, actionLabel, uiSignal, uiSettings = {}, loc, selectedWeights, setSelectedWeights, actions, borrowingAllowed = true, bankingAllowed = true, stashAllowed = true, settingsOpen = false, serverInfoOpen = false, accountSettingsProps = null, serverInfo = null, serverInfoBusy = false }) {
  const [openWindows, setOpenWindows] = useState([]);
  const [settingsApplying, setSettingsApplying] = useState(false);
  const settingsApplyTimerRef = useRef(null);
  const [sceneColumnHeight, setSceneColumnHeight] = useState(0);
  const hudRef = useRef(null);
  const sceneColumnRef = useRef(null);
  const currentLocation = useMemo(() => locationMeta(gameState.currentLocationId), [gameState.currentLocationId]);
  const currentNeighborhood = neighborhoodForPresentation(gameState.currentLocationId);
  const world = gameState.worldState || {};
  const pulse = Array.isArray(world.worldPulse) ? world.worldPulse.slice(0, 2) : [];
  const rumors = Array.isArray(world.rumors) ? world.rumors.slice(0, 1) : [];
  const tags = locationPublicTags(currentLocation, gameState);
  const buyPrices = gameState.marketBuyPrices || gameState.marketPrices || {};
  const sellPrices = gameState.marketSellPrices || gameState.marketPrices || {};
  const hasMarket = !loc?.isHome && !loc?.isSceneLocation && !loc?.isPawnShop && PRODUCTS.some((product) => buyPrices?.[product.id] != null || sellPrices?.[product.id] != null);
  const hasHustles = Array.isArray(gameState?.hustles?.items) && gameState.hustles.items.length > 0;
  const homeArt = currentLocation.id === 'home' ? homeArtForState(gameState) : null;
  const capacityUsed = inventoryCapacityUsed(gameState.inventory || {});

  const focusWindow = (name) => setOpenWindows((current) => [...current.filter((id) => id !== name), name]);
  const openWindow = (name) => {
    focusWindow(name);
    if (name === 'settings') actions.openSettings?.();
    if (name === 'server') actions.openServerInfo?.();
  };
  const closeWindow = (name) => {
    setOpenWindows((current) => current.filter((id) => id !== name));
    if (name === 'settings') actions.closeSettings?.();
    if (name === 'server') actions.closeServerInfo?.();
  };

  const handleSettingsApplied = () => {
    if (typeof window === 'undefined') { closeWindow('settings'); return; }
    setSettingsApplying(true);
    if (settingsApplyTimerRef.current) window.clearTimeout(settingsApplyTimerRef.current);
    settingsApplyTimerRef.current = window.setTimeout(() => {
      setSettingsApplying(false);
      closeWindow('settings');
      settingsApplyTimerRef.current = null;
    }, 750);
  };

  useEffect(() => () => {
    if (settingsApplyTimerRef.current && typeof window !== 'undefined') window.clearTimeout(settingsApplyTimerRef.current);
  }, []);

  useEffect(() => {
    setOpenWindows((current) => settingsOpen
      ? (current.includes('settings') ? current : [...current, 'settings'])
      : current.filter((id) => id !== 'settings'));
    if (!settingsOpen) setSettingsApplying(false);
  }, [settingsOpen]);

  useEffect(() => {
    setOpenWindows((current) => serverInfoOpen
      ? (current.includes('server') ? current : [...current, 'server'])
      : current.filter((id) => id !== 'server'));
  }, [serverInfoOpen]);

  useEffect(() => {
    if (typeof window === 'undefined' || !openWindows.includes('server')) return undefined;
    const refresh = () => actions.refreshServerInfo?.();
    const timer = window.setInterval(refresh, 15000);
    return () => window.clearInterval(timer);
  }, [openWindows, actions]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleOpenWindow = (event) => {
      const id = String(event.detail?.id || '');
      if (id) openWindow(id);
    };
    window.addEventListener('dopeman-v2-open-window', handleOpenWindow);
    return () => window.removeEventListener('dopeman-v2-open-window', handleOpenWindow);
  });


  useEffect(() => {
    if (!hudRef.current || typeof document === 'undefined') return undefined;
    const update = () => document.documentElement.style.setProperty('--v2-hud-height', `${Math.ceil(hudRef.current.getBoundingClientRect().height)}px`);
    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(hudRef.current);
    window.addEventListener('resize', update);
    return () => { observer?.disconnect(); window.removeEventListener('resize', update); document.documentElement.style.removeProperty('--v2-hud-height'); };
  }, []);

  useEffect(() => {
    if (!sceneColumnRef.current) return undefined;
    const update = () => setSceneColumnHeight(Math.ceil(sceneColumnRef.current?.getBoundingClientRect().height || 0));
    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(sceneColumnRef.current);
    window.addEventListener('resize', update);
    return () => { observer?.disconnect(); window.removeEventListener('resize', update); };
  }, [gameState.currentLocationId, world.timeFlavor, world.currentLocationState?.text]);

  const sceneArt = homeArt
    ? { src: homeArt.banner, alt: `${homeArt.name} player home` }
    : currentNeighborhood?.id === 'mt_gate'
      ? { src: './world/neighborhoods/mt-gate-banner.webp', alt: 'Mt. Gate neighborhood' }
      : null;

  const windowSpecFor = (name) => name === 'market'
    ? { title: `MARKET — ${currentLocation.shortName.toUpperCase()}`, subtitle: hasMarket ? 'BUY LOW · SELL HIGH' : 'NO MARKET AT THIS STOP', initialWidth: 880, initialHeight: 660, minWidth: 520, minHeight: 420 }
    : name === 'travel'
      ? { title: 'WORLD NAVIGATOR', subtitle: `${currentLocation.shortName} · ${periodLabel(gameState)}`, initialWidth: 820, initialHeight: 650, minWidth: 500, minHeight: 420 }
      : name === 'hustles'
        ? { title: 'HUSTLES', subtitle: `${currentLocation.shortName} · questionable side work`, initialWidth: 860, initialHeight: 610, minWidth: 500, minHeight: 400 }
        : name === 'notebook'
          ? { title: 'MARKET NOTEBOOK', subtitle: 'LAST-SEEN PRICES', initialWidth: 790, initialHeight: 620, minWidth: 470, minHeight: 360 }
          : name === 'safehouse'
            ? { title: 'SAFEHOUSE', subtitle: currentLocation.id === 'home' ? 'BANK · STASH · CAREER' : 'HOME SERVICES OFF-SITE', initialWidth: 860, initialHeight: 680, minWidth: 520, minHeight: 400 }
            : name === 'history'
              ? { title: 'RUN HISTORY', subtitle: `DAY ${gameState.day} · ADVENTURE LOG`, initialWidth: 760, initialHeight: 690, minWidth: 460, minHeight: 380 }
              : name === 'bulletin'
                ? { title: 'BULLETIN BOARD', subtitle: gameState?.bulletinBoard?.title || 'CORNER STORE NOTICES', initialWidth: 780, initialHeight: 590, minWidth: 460, minHeight: 340 }
                : name === 'settings'
                  ? { title: 'SETTINGS', subtitle: session?.player?.alias || 'player preferences', initialWidth: 930, initialHeight: 700, minWidth: 620, minHeight: 460 }
                  : name === 'server'
                    ? { title: 'SERVER STATUS', subtitle: `${hubState} · ${config.appVersion}`, initialWidth: 660, initialHeight: 570, minWidth: 440, minHeight: 360 }
                    : { title: 'INVENTORY', subtitle: `PACK ${capacityUsed.toFixed(1).replace(/\.0$/, '')}/${gameState.capacity}g`, initialWidth: 720, initialHeight: 560, minWidth: 460, minHeight: 360 };


  const windowActions = {
    ...actions,
    openSettings: () => openWindow('settings'),
    openServerInfo: () => openWindow('server'),
    openTravel: () => openWindow('travel'),
    openNotebook: () => openWindow('notebook'),
    openSafehouse: () => openWindow('safehouse'),
    openRunHistory: () => openWindow('history'),
    openBulletin: () => openWindow('bulletin'),
  };

  return <div className={`v2-shell ${actionBusy ? 'busy' : ''}`}>
    <div className="v2-game-cabinet">
      <header className="v2-hud" ref={hudRef}>
        <button className="v2-brand" onClick={() => window.scrollTo?.({ top: 0, behavior: uiSettings.reduced_motion ? 'auto' : 'smooth' })}><img src="./branding/the-dope-man-icon.png" alt="" /><span><strong>THE DOPE MAN</strong><small>UI v2 · PREVIEW 9</small></span></button>
        <div className="v2-hud-stats"><Stat label="DAY" value={gameState.day} /><Stat label="HP" value={wholeHealth(gameState.health)} /><Stat label="REP" value={gameState.streetRep} /><Stat label="CASH" value={money(gameState.cash)} tone="cash" /><Stat label="DEBT" value={money(gameState.debt)} tone="debt" /></div>
        <div className="v2-hud-actions"><button onClick={() => openWindow('travel')} title="World"><Compass /></button><button onClick={() => openWindow('server')} title="Server information"><Server /></button><button onClick={() => openWindow('settings')} title="Account settings"><CircleUserRound /></button></div>
        <div className="v2-hud-context"><span><MapPin />{currentLocation.shortName}</span><span><WalletCards />Bank {money(gameState.bankCash)}</span><span><PackageOpen />{capacityUsed.toFixed(1).replace(/\.0$/, '')}/{gameState.capacity}g</span><span className={hubState === 'ONLINE' ? 'online' : 'warning'}>{hubState === 'ONLINE' ? <Wifi /> : <AlertTriangle />}{hubState}</span></div>
      </header>

      <main className="v2-main">
        {uiSignal?.message && <div className={`v2-feedback-banner tone-${uiSignal.category || 'system'}`}><strong>{uiSignal.title || 'UPDATE'}</strong><span>{uiSignal.message}</span></div>}

        <section className="v2-adventure-workspace">
          <div className="v2-scene-column" ref={sceneColumnRef}>
            <div className="v2-scene-viewport">
              {sceneArt ? <img key={`${gameState.currentLocationId}-${sceneArt.src}`} className="v2-scene-image" src={sceneArt.src} alt={sceneArt.alt} loading="eager" /> : <NeighborhoodArt meta={currentNeighborhood || { name: 'On the road', placeholder: 'generic' }} mode="banner" />}
              <div className="v2-scene-stamp"><span>{periodLabel(gameState)}</span><strong>DAY {gameState.day}</strong></div>
            </div>
            <div className="v2-location-narrative">
              <div className="v2-location-heading"><h1>{currentLocation.shortName}</h1><span>{periodLabel(gameState)}</span></div>
              <p>{world.timeFlavor || currentLocation.description}</p>
              <StatusTags tags={tags} />
              {world.currentLocationState?.text && <blockquote>{world.currentLocationState.text}</blockquote>}
              {(pulse.length > 0 || rumors.length > 0) && <div className="v2-street-noise">
                <span>STREET NOISE</span>
                {pulse.slice(0, 1).map((row) => <article key={row.id || row.headline}><b className={`intel-${certaintyLabel(row).toLowerCase()}`}>{certaintyLabel(row)}</b><strong>{row.headline}</strong><p>{row.detail}</p></article>)}
                {rumors.map((row, index) => <article key={row.id || `rumor-${index}`}><b className="intel-rumor">RUMOR</b><strong>{row.text || row.headline}</strong><p>{row.source || 'Street gossip'}{row.expiresDay ? ` · expires Day ${row.expiresDay}` : ''}</p></article>)}
              </div>}
            </div>
          </div>
          <V2AdventurePanel gameState={gameState} uiSettings={uiSettings} actions={windowActions} desktopHeight={sceneColumnHeight} />
        </section>

        <section className="v2-command-console" aria-label="Context actions">
          <div className="v2-verb-rail">
            <button onClick={actions.lookAround} disabled={actionBusy} title="Look around"><Sparkles /><span>LOOK</span></button>
            <button onClick={() => openWindow('market')} disabled={!hasMarket || actionBusy} title={hasMarket ? 'Open market window' : 'Nothing is trading here'}><ShoppingCart /><span>MARKET</span></button>
            <button onClick={() => openWindow('hustles')} disabled={!hasHustles || actionBusy} title={hasHustles ? 'Open hustles window' : 'No hustles available'}><BriefcaseBusiness /><span>HUSTLE</span></button>
            <button onClick={() => openWindow('travel')} title="Open travel window"><Navigation /><span>TRAVEL</span></button>
            <button onClick={() => openWindow('notebook')} title="Open market notebook"><BookOpen /><span>NOTES</span></button>
            {currentLocation.id === 'home' && <button onClick={() => openWindow('safehouse')} title="Open safehouse tools"><Home /><span>SAFEHOUSE</span></button>}
            <button onClick={() => openWindow('inventory')} title="Open inventory window"><Wrench /><span>STUFF</span></button>
          </div>
        </section>

        <InventoryStrip gameState={gameState} capacityUsed={capacityUsed} onOpen={() => openWindow('inventory')} />

        <section className="v2-preview-note"><strong>UI v2 · PREVIEW 9 · FINISHING PASS</strong><div><button onClick={() => openWindow('settings')}>UI settings</button><button onClick={() => actions.useStableInterface?.('street')}>Stable UI</button></div></section>
        <footer>The Dope Man · UI v2 Preview 9 · Client {config.appVersion} · {runMeta?.ruleset || gameState.eventPack || 'local'}</footer>
      </main>
    </div>

    {actionBusy && <div className="v2-action-progress"><i /><span>{actionLabel || 'SAVING'}</span></div>}

    {openWindows.map((windowId, index) => {
      const spec = windowSpecFor(windowId);
      const focused = index === openWindows.length - 1;
      return <GameWindow
        key={windowId}
        id={windowId}
        title={spec.title}
        subtitle={spec.subtitle}
        onClose={() => closeWindow(windowId)}
        onFocus={() => focusWindow(windowId)}
        zIndex={9000 + index}
        isFocused={focused}
        initialWidth={spec.initialWidth}
        initialHeight={spec.initialHeight}
        minWidth={spec.minWidth}
        minHeight={spec.minHeight}
        className={`v2-window-${windowId}`}
      >
        {windowId === 'market' && (hasMarket ? <Suspense fallback={<div className="v2-lazy-loading"><i />Loading market tools…</div>}><V2MarketView gameState={gameState} loc={loc} actionBusy={actionBusy} selectedWeights={selectedWeights} setSelectedWeights={setSelectedWeights} actions={windowActions} onBack={() => closeWindow('market')} embedded /></Suspense> : <div className="v2-window-unavailable"><ShoppingCart /><strong>No active street market here.</strong><span>The Market window stayed open when your location changed. Travel somewhere with an active market and it will refresh in place.</span><button type="button" onClick={() => openWindow('travel')}>OPEN TRAVEL</button></div>)}
        {windowId === 'travel' && <TravelWindowContent gameState={gameState} onTravel={actions.travel} busy={actionBusy} />}
        {windowId === 'inventory' && <V2InventoryWindow gameState={gameState} actionBusy={actionBusy} actions={windowActions} />}
        {windowId === 'hustles' && <V2HustleWindow hustles={gameState.hustles} busy={actionBusy} onSearch={actions.hustleSearch} />}
        {windowId === 'notebook' && <V2NotebookWindow gameState={gameState} />}
        {windowId === 'safehouse' && <V2SafehouseWindow gameState={gameState} loc={loc} actionBusy={actionBusy} selectedWeights={selectedWeights} setSelectedWeights={setSelectedWeights} bankingAllowed={bankingAllowed} stashAllowed={stashAllowed} borrowingAllowed={borrowingAllowed} actions={windowActions} />}
        {windowId === 'history' && <V2RunHistoryWindow gameState={gameState} uiSettings={uiSettings} />}
        {windowId === 'bulletin' && <V2BulletinWindow board={gameState.bulletinBoard || {}} />}
        {windowId === 'settings' && (settingsApplying
          ? <div className="v2-settings-applying" role="status" aria-live="polite"><i /><strong>APPLYING SETTINGS…</strong><span>Updating the interface.</span></div>
          : accountSettingsProps ? <AccountSettings {...accountSettingsProps} onClose={() => closeWindow('settings')} onApplied={handleSettingsApplied} onResetWindowLayout={() => resetGameWindowGeometry()} embedded /> : <div className="v2-window-unavailable">Settings are unavailable in this session.</div>)}
        {windowId === 'server' && <ServerInfoModal open embedded info={serverInfo} clientVersion={config.appVersion} busy={serverInfoBusy} onRefresh={actions.refreshServerInfo} onClose={() => closeWindow('server')} />}
      </GameWindow>;
    })}
  </div>;
}
